import { EmployeeRole } from '@/types';
import { PermissionAction, PermissionSubject } from '@/enums';

export type BusinessEmployeePermissions = {
  [key in PermissionSubject]: PermissionAction | boolean;
};

/**
 * Business employee access control factory
 *
 * @param permissions {BusinessEmployeePermissions} - The business employee's permissions
 * @returns {can} A utility to check if the business employee can perform an action on a subject
 *
 * @example Action-based permissions (Read/Write)
 * const can = businessEmployeeAccessControlFactory(permissions);
 * const canEdit = can(PermissionAction.Write, PermissionSubject.SrtPriceEditing);
 * const canView = can(PermissionAction.Read, PermissionSubject.SrtPriceEditing);
 *
 * @example Boolean permissions (true/false)
 * // Some subjects use boolean permissions — any action check returns the boolean value.
 * const canViewCommissions = can(PermissionAction.Read, PermissionSubject.Commissions);
 */
export const businessEmployeeAccessControlFactory = (
  permissions?: Partial<BusinessEmployeePermissions>,
) => {
  if (!permissions) {
    return (_action: PermissionAction, _subject: PermissionSubject) => false;
  }

  /**
   * Helper utility for business employee access control
   *
   * @callback can
   * @param action {PermissionAction} - The action to check for permission for
   * @param subject {PermissionSubject} - The subject which you would like to check the business employee action on
   * @returns {boolean} True if the business employee can perform the action on the subject, false otherwise
   */
  return (action: PermissionAction, subject: PermissionSubject) => {
    const value = permissions[subject];

    if (!value) {
      return false;
    }

    // Boolean permissions (e.g. allow_commission: true)
    if (typeof value === 'boolean') {
      return value;
    }

    if (action === PermissionAction.Write) {
      return value === PermissionAction.Write;
    } else if (action === PermissionAction.Read) {
      return (
        value === PermissionAction.Read || value === PermissionAction.Write
      );
    }

    return false;
  };
};

export enum EmployeeAccessLevel {
  User = 0,
  Dsm = 1,
  SupplierDefault = 2,
  Admin = 3,
}

const EmployeeRoleToAccessLevelMap: Record<EmployeeRole, EmployeeAccessLevel> =
  {
    [EmployeeRole.Cashier]: EmployeeAccessLevel.User,
    [EmployeeRole.User]: EmployeeAccessLevel.User,
    [EmployeeRole.Dsm]: EmployeeAccessLevel.Dsm,
    [EmployeeRole.SupplierDefault]: EmployeeAccessLevel.Admin, // Supplier default gets admin access in PMC
    [EmployeeRole.Admin]: EmployeeAccessLevel.Admin,
  };

const verifyEmployeeAccess = (
  employeeAccessLevel: EmployeeAccessLevel,
  resourceAccessLevel: EmployeeAccessLevel,
) => {
  return employeeAccessLevel >= resourceAccessLevel;
};

// null indicates access check is pending
export type AccessCheckFn = (
  supplierEmployeeRole: EmployeeRole | null,
) => boolean | null;

export const useSupplierDsmOrHigherCheck: AccessCheckFn = (
  supplierEmployeeRole,
) => {
  return Boolean(
    supplierEmployeeRole &&
      verifyEmployeeAccess(
        EmployeeRoleToAccessLevelMap[supplierEmployeeRole],
        EmployeeAccessLevel.Dsm,
      ),
  );
};
